import type { Platform, PlatformPackage } from '@/types'
import {
  hasPackageFile,
  hasReadyPackageFile,
  isBackendHistoricalPackage,
} from '@/pages/app-detail/versions/platform'

/**
 * 这次会话里主包替换过子包列表的版本。
 * 之后的详情不能把替换前的非历史子包再合并回来；历史包仍可以补上。
 * 只活在内存里，刷新页面后以服务端详情为准。
 */
const replacedPlatformVersions = new Set<string>()

export function markPlatformPackagesReplaced(versionId: string) {
  if (versionId) replacedPlatformVersions.add(versionId)
}

export function hasPlatformPackagesReplaced(versionId: string) {
  return replacedPlatformVersions.has(versionId)
}

export function resetPlatformPackageReplaceMarks() {
  replacedPlatformVersions.clear()
}

/**
 * 主包已经替换过子包列表之后，用详情合并：
 * 留下本地这份列表（含之后新传的子包），只补远端的历史包。
 * 远端多出来的非历史子包是上一份主包留下的，不再装回来。
 */
export function mergeReplacedPlatformPackages(
  local: PlatformPackage[] | undefined,
  remote: PlatformPackage[] | undefined,
  declaredPlatforms: Platform[] = [],
): PlatformPackage[] {
  const declared = new Set(declaredPlatforms)
  const inScope = (p: Platform) => declared.size === 0 || declared.has(p)
  const byPlatform = new Map<Platform, PlatformPackage>()
  for (const pkg of local ?? []) {
    if (inScope(pkg.platform)) byPlatform.set(pkg.platform, pkg)
  }
  for (const pkg of remote ?? []) {
    if (!inScope(pkg.platform) || !isBackendHistoricalPackage(pkg)) continue
    const cur = byPlatform.get(pkg.platform)
    if (
      cur &&
      !cur.is_historical &&
      (cur.uploaded_locally || hasPackageFile(cur))
    ) {
      continue
    }
    byPlatform.set(pkg.platform, pkg)
  }
  return [...byPlatform.values()]
}

/**
 * 用详情覆盖本地 platform_packages 时的合并规则：
 * - 详情不带文件名 / 大小：同一个包沿用本地值
 * - 本次会话刚上传成功（uploaded_locally）而详情还没确认：保留本地
 * - parse-result 写入的历史包（带 from_version_id），详情仍是占位项时：保留本地
 * - 直传 pending / failed（有 upload_id 但未挂上）不是占位，不能用本地历史包盖掉
 * - 只保留 / 补回当前声明平台内的本地项，避免重传主包后残留已移除的平台
 */
export function keepLocalPackageMeta(
  local: PlatformPackage[] | undefined,
  remote: PlatformPackage[] | undefined,
  declaredPlatforms: Platform[] = [],
): PlatformPackage[] | undefined {
  if (!local?.length) return remote
  const declared = new Set(declaredPlatforms)
  const inScope = (p: Platform) => declared.size === 0 || declared.has(p)
  const keepable = (l: PlatformPackage) =>
    inScope(l.platform) &&
    ((l.uploaded_locally && !l.is_historical) || isBackendHistoricalPackage(l))

  // 接口没带 platform_packages 时不能把本地刚上传 / 沿用的项整体清掉
  if (!remote) {
    const kept = local.filter(keepable)
    return kept.length ? kept : undefined
  }

  const merged = remote.map((r) => {
    const l = local.find((x) => x.platform === r.platform)
    if (!l) return r
    const sameRecord =
      l.is_historical === r.is_historical &&
      (!l.upload_id || !r.upload_id || l.upload_id === r.upload_id)
    // 已挂上 ≠ 空占位：pending 有 upload_id，不能当占位被历史包覆盖
    const remoteUploaded = !r.is_historical && hasReadyPackageFile(r)
    const remotePlaceholder = !r.is_historical && !hasPackageFile(r)
    if (
      keepable(l) &&
      l.uploaded_locally &&
      !l.is_historical &&
      (!remoteUploaded || !sameRecord)
    ) {
      return { ...r, ...l }
    }
    if (keepable(l) && l.is_historical && remotePlaceholder) {
      return { ...r, ...l }
    }
    if (!sameRecord) return r
    return {
      ...r,
      file_name: r.file_name ?? l.file_name,
      size_mb: r.size_mb ?? l.size_mb,
    }
  })
  const missing = local.filter(
    (l) => keepable(l) && !merged.some((r) => r.platform === l.platform),
  )
  return missing.length ? [...merged, ...missing] : merged
}
